import { SigninCardFull } from '@ordo/ui/signin-card-full'
import { usePostHog } from '@posthog/react'
import { useSearchParams } from 'react-router'

import { PublicShell } from '@/components/public-shell'

/**
 * Sign-in page (Plan 50). Renders the shared `SigninCardFull` primitive
 * from `@ordo/ui` and wires its callbacks to apps/ordo's server routes.
 * Form state lives inside the primitive; this consumer is a thin
 * callback shim.
 *
 * `?email=` (from invite emails) seeds the email field, and while it's
 * present the "New to Ordo?" link routes to `/signup` — the invited
 * account-claim page — instead of the school-first `/get-started`
 * funnel, carrying the email along.
 */
export function SignIn() {
  const posthog = usePostHog()
  const [params] = useSearchParams()
  const inviteEmail = params.get('email')?.trim() ?? ''
  const getStartedHref = inviteEmail ? `/signup?email=${encodeURIComponent(inviteEmail)}` : '/get-started'

  async function onSendMagicLink(email: string) {
    const res = await fetch('/api/auth/magic-link', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    // Enumeration protection: server returns { ok: true } for known +
    // unknown emails alike. Surface the same UX either way.
    if (res.ok) {
      posthog.capture('magic_link_requested')
      return { ok: true as const }
    }
    return { ok: false as const, error: 'Could not send link. Try again.' }
  }

  async function onSignInWithCredentials(email: string, password: string) {
    const res = await fetch('/api/auth/credentials', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (res.ok) {
      const body = (await res.json().catch(() => ({}))) as { redirectTo?: string }
      return { ok: true as const, redirectTo: body.redirectTo ?? '/' }
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    return { ok: false as const, error: body.error ?? 'Invalid email or password.' }
  }

  return (
    <PublicShell center showAnnouncements>
      {/* No `loginHref` — that hides the "Continue with Google" CTA. Google
          OAuth only resolves internal Ordo accounts and ordo's users are
          mostly external, so the button was a dead end. Magic link +
          password are the sign-in paths. */}
      <SigninCardFull
        appKey="ordo"
        getStartedHref={getStartedHref}
        initialEmail={inviteEmail || undefined}
        onSendMagicLink={onSendMagicLink}
        onSignInWithCredentials={onSignInWithCredentials}
      />
    </PublicShell>
  )
}
